import React, { type FC, use, useCallback, useMemo, useState } from "react";
import { Button, Checkbox, cn, Label, Modal, Spinner } from "@heroui/react";
import { Check } from "@gravity-ui/icons";
import { FormattedMessage } from "react-intl";
import { Link } from "react-router-dom";
import { GQL } from "@binale-tech/shared";
import { useMutation } from "@apollo/client/react";

import { AppRoutes } from "@domain/routing/routeConstants";
import { CompanyContext, CompanyControlContext } from "@domain/context/CompanyContext";
import { UserContext } from "@domain/context/UserProvider";
import { legalAcceptanceRecord } from "@domain/graphql/legalAcceptance";

// New tab on purpose: navigating in place would drop the modal state.
const AVV_VALUES = {
    avvLink: (
        <Link to={AppRoutes.avv} target="_blank" rel="noopener noreferrer">
            <FormattedMessage id="app.page.legal.avv" />
        </Link>
    ),
};

const dismissKey = (companyId: string) => `avvModalDismissed:${companyId}`;

/** Owner-only prompt to accept the AVV while the active company has none recorded. */
export const AvvAcceptanceModal: FC = () => {
    const user = use(UserContext);
    const { companyGQL } = use(CompanyContext);
    const { triggerDataRefetch } = use(CompanyControlContext);

    const [isChecked, setIsChecked] = useState(false);
    // mirrors the sessionStorage marker so dismissing re-renders
    const [dismissedNow, setDismissedNow] = useState<string | null>(null);

    const [legalAcceptanceRecordMutation, { loading, error }] = useMutation(legalAcceptanceRecord);

    const companyId = companyGQL?.id;
    const isOwner = Boolean(companyId) && companyGQL.owner?.id === user.fireUser?.uid;
    const isSessionDismissed =
        !companyId || dismissedNow === companyId || sessionStorage.getItem(dismissKey(companyId)) === "1";
    const isOpen = isOwner && !companyGQL.avvAccepted && !isSessionDismissed;

    const dismiss = useCallback(() => {
        if (companyId) {
            sessionStorage.setItem(dismissKey(companyId), "1");
            setDismissedNow(companyId);
        }
    }, [companyId]);

    const onOpenChange = useCallback(
        (open: boolean) => {
            if (!open) {
                dismiss();
            }
        },
        [dismiss],
    );

    const accept = useCallback(async () => {
        if (!companyId) {
            return;
        }
        await legalAcceptanceRecordMutation({
            variables: { input: { documentType: GQL.ILegalDocumentType.Avv, companyId } },
        });
        // hide modal immediately to avoid flickering during reload
        sessionStorage.setItem(dismissKey(companyId), "1");
        // the refetch flips avvAccepted, which closes the modal
        await triggerDataRefetch();
    }, [companyId, legalAcceptanceRecordMutation, triggerDataRefetch]);

    const descriptionValues = useMemo(
        () => ({ companyName: companyGQL?.name ?? "", ...AVV_VALUES }),
        [companyGQL?.name],
    );

    if (!isOpen) {
        return null;
    }
    return (
        <Modal.Backdrop isOpen isDismissable={false} isKeyboardDismissDisabled onOpenChange={onOpenChange}>
            <Modal.Container size="lg">
                <Modal.Dialog data-testid="avv-acceptance-modal">
                    <Modal.Header>
                        <Modal.Heading>
                            <FormattedMessage id="app.page.legal.avv" />
                        </Modal.Heading>
                    </Modal.Header>
                    <Modal.Body className="flex flex-col gap-4">
                        <p className="text-sm text-muted">
                            <FormattedMessage id="app.avv.modal.description" values={descriptionValues} />
                        </p>
                        <Checkbox
                            isSelected={isChecked}
                            onChange={setIsChecked}
                            data-testid="avv-modal-accept-checkbox"
                        >
                            <Checkbox.Content>
                                <Checkbox.Control className={cn({ "border border-border-tertiary": !isChecked })}>
                                    <Checkbox.Indicator />
                                </Checkbox.Control>
                                <Label>
                                    <FormattedMessage id="app.avv.accept" values={AVV_VALUES} />
                                </Label>
                            </Checkbox.Content>
                        </Checkbox>
                        {error && <span className="text-danger text-xs">{error.message}</span>}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="tertiary" onPress={dismiss} isDisabled={loading} data-testid="avv-modal-later">
                            <FormattedMessage id="app.button.later" />
                        </Button>
                        <Button
                            isDisabled={!isChecked}
                            isPending={loading}
                            onPress={accept}
                            data-testid="avv-modal-confirm"
                        >
                            {({ isPending }) => (
                                <>
                                    {isPending ? <Spinner color="current" size="sm" /> : <Check className="size-4" />}
                                    <FormattedMessage id="app.button.confirm" />
                                </>
                            )}
                        </Button>
                    </Modal.Footer>
                </Modal.Dialog>
            </Modal.Container>
        </Modal.Backdrop>
    );
};
